import { sortBy, startCase } from "lodash-es";
import { create as createStore } from "zustand";
import { GameEngine } from "~/engine/instance";
import { RENDER_MODE } from "~/renderers/three-renderer";
import { firestoreService, nowTimestamp } from "~/services/firestore-service";
import type {
  ChunkSerialized,
  InventoryMineral,
  PlayerStats,
  Vector2,
} from "~/state/game-types";
import { TERRAIN_TYPES_MAP } from "~/terrain/types";
import { toast } from "~/ui/system/toast";
import { getStorage } from "~/utils/storage";

export type GameStoreValues = {
  /** @deprecated use the page url instead */
  gameStarted: boolean;

  // metadata
  openMenu: "pause" | "inventory" | "settings" | "login" | null;
  user: { id: string; username: string } | null;
  currentGameSaveId: string | null;
  gameSaves: Awaited<ReturnType<typeof firestoreService.loadGameSaves>>;
  baseSeed: number;

  // Settings
  settings: {
    renderMode: RENDER_MODE;
    isAdmin: boolean;
  };

  // Player state
  playerPosition: Vector2;
  playerStats: PlayerStats;
  playerUpgrades: PlayerUpgradeValues;

  // Inventory and economy
  inventory: InventoryMineral[];
  currency: number;

  // chunks
  initialChunks: { [uid in number]: ChunkSerialized };
};

export type GameStoreActions = {
  // game actions
  updatePlayerPosition: (position: Vector2) => void;
  updatePlayerStats: (stats: Partial<PlayerStats>) => void;
  addToInventory: (items: { [id in number]: number }) => void;
  sellMineral: (itemId: number, quantity: number) => number;
  purchaseUpgrade: (
    upgradeKey: PlayerUpgradeNumberKey,
    amount: number,
  ) => boolean;
  setLoadout: (loadoutNumber: number) => void;

  // Settings actions
  setOpenMenu: (menu: GameStoreValues["openMenu"]) => void;
  updateSettings: (settings: Partial<GameStoreValues["settings"]>) => void;
  updatePlayerUpgrade: <Key extends PlayerUpgradeKey>(
    upgrade: Key,
    value: PlayerUpgradeValues[Key],
  ) => void;

  // Life cycle + saving + metadata
  startGame: (gameSaveId?: string) => Promise<string>;
  loadUserData: () => Promise<void>;
  setUser: (user: { id: string; username: string } | null) => Promise<void>;
  saveGame: () => Promise<void>;

  // chunks
  // updateChunk: (chunk: ChunkSerialized) => void;
};

export const PLAYER_UPGRADES = {
  miningMode: {
    default: "circle",
    options: ["circle", "laser", "grenade"] as const,
    label: "Mining Mode",
  },
  drillStrength: {
    default: 1 as number,
    min: 1,
    max: 10,
    step: 1,
    label: "Drill Strength",
  },
  drillRadius: {
    default: 18 as number, // just under 5 * 8 (`voxelSize`)
    min: 10,
    max: 100,
    step: 5,
    label: "Drill Radius",
  },
  drillOffset: {
    default: 24 as number,
    min: 10,
    max: 1000,
    step: 2,
    label: "Drill Offset",
  },
  cooldownRate: {
    default: 0.5 as number,
    min: 0.1,
    max: 20.0,
    step: 0.1,
    label: "Cooldown Rate",
  },
  heatResistance: {
    default: 20.0 as number,
    min: 10,
    max: 1000,
    step: 5,
    label: "Heat Resistance",
  },
  movementSpeed: {
    default: 400 as number,
    min: 100,
    max: 1000,
    step: 50,
    label: "Movement Speed",
  },
  fuelCapacity: {
    default: 100 as number,
    min: 100,
    max: 10000,
    step: 100,
    label: "Fuel Capacity",
  },
  // /** TODO: unused */
  // storageCapacity: {
  //   default: 60,
  //   min: 60,
  //   max: 1000,
  //   step: 10,
  //   label: "Storage Capacity",
  // },
} as const;

export type PlayerUpgradeKey = keyof typeof PLAYER_UPGRADES;
export type PlayerUpgradeValues = {
  [k in PlayerUpgradeKey]: (typeof PLAYER_UPGRADES)[k] extends {
    options: infer T extends readonly string[];
  }
    ? T[number]
    : (typeof PLAYER_UPGRADES)[k]["default"];
};

const PLAYER_UPGRADES_DEFAULTS = Object.fromEntries(
  Object.entries(PLAYER_UPGRADES).map(([key, value]) => [key, value.default]),
) as PlayerUpgradeValues;

type PlayerUpgradeNumberKey = {
  [k in PlayerUpgradeKey]: PlayerUpgradeValues[k] extends number ? k : never;
}[PlayerUpgradeKey];

function getRandomSeed() {
  return Math.floor(Math.random() * 100000);
}

const blankGameStoreValues = (): GameStoreValues => ({
  playerPosition: { x: 0, y: 0 },
  playerStats: {
    health: 100,
    fuel: 100,
    heat: 0,
  },
  playerUpgrades: { ...PLAYER_UPGRADES_DEFAULTS },
  inventory: [],
  currency: 0,
  openMenu: null,
  gameStarted: false,
  user: null,
  currentGameSaveId: null,
  gameSaves: [],
  settings: {
    renderMode: RENDER_MODE.ORTHO,
    isAdmin: false,
  },
  initialChunks: {},
  baseSeed: getRandomSeed(),
});

export type GameStore = typeof useGameStore;

export const useGameStore = createStore<GameStoreValues & GameStoreActions>(
  (set, get) => ({
    ...blankGameStoreValues(),

    setUser: async (user: { id: string; username: string } | null) => {
      set({ user });

      getStorage().setItem("mining_game:player_id", user?.id || "");

      let gameSaves;
      if (user) {
        try {
          gameSaves = await firestoreService.loadGameSaves(user.id);

          gameSaves = sortBy(
            gameSaves,
            (d) =>
              -Math.max(
                d.last_launched_at?.toMillis() ?? 0,
                d.created_at.toMillis(),
              ),
          );
        } catch (err) {
          console.error(`Failed to load gameSaves for user ${user.id}`, err);
        }
      }
      set({ gameSaves: gameSaves || [] });
    },

    loadUserData: async () => {
      const initialPlayerId = getStorage().getItem("mining_game:player_id");
      if (!initialPlayerId) return;

      const playerData = await firestoreService.getUser(initialPlayerId);
      if (!playerData) return;

      await get().setUser({
        id: initialPlayerId,
        username: playerData.username,
      });
    },

    startGame: async (gameSaveId?: string) => {
      let user = get().user;
      if (!user) await get().loadUserData();
      user = get().user;
      if (!user) throw new Error("No user found");

      // first, reset the store
      set(blankGameStoreValues());

      let initStoreData: Partial<GameStoreValues> | undefined;
      if (gameSaveId) {
        const dbGameSave = await firestoreService.loadGameSave(
          user.id,
          gameSaveId,
        );
        if (!dbGameSave) throw new Error("gameSave not found");
        const initialChunks = await firestoreService.loadChunks(
          user.id,
          gameSaveId,
        );
        initStoreData = {
          initialChunks: Object.fromEntries(
            initialChunks.map((chunk) => [chunk.uid, chunk]),
          ),
          ...deserializeGameState(dbGameSave),
        };
      } else {
        gameSaveId = (
          await firestoreService.saveGame(
            user.id,
            null,
            serializeGameState(get()),
            null,
          )
        ).id;
      }

      await firestoreService.updateGameSave(user.id, gameSaveId, {
        last_launched_at: nowTimestamp(),
      });

      set({
        ...(initStoreData || {}),
        user,
        currentGameSaveId: gameSaveId,
      });

      return gameSaveId;
    },

    updatePlayerPosition: (position: Vector2) => {
      set({ playerPosition: position });
    },

    updatePlayerStats: (stats: Partial<PlayerStats>) => {
      set((state) => ({
        playerStats: { ...state.playerStats, ...stats },
      }));
    },

    addToInventory: (items: { [id in number]: number }) => {
      set((state) => {
        const updatedInventory = [...state.inventory];

        // Process each item in the items object
        Object.entries(items).forEach(([id, quantity]) => {
          const itemId = Number(id);
          const existingItemIndex = updatedInventory.findIndex(
            (i) => i.id === itemId,
          );

          if (existingItemIndex !== -1) {
            // Update existing item
            updatedInventory[existingItemIndex] = {
              ...updatedInventory[existingItemIndex]!,
              quantity:
                updatedInventory[existingItemIndex]!.quantity + quantity,
            };
          } else {
            // Add new item
            updatedInventory.push({
              id: itemId,
              quantity,
            });
          }
        });

        return { inventory: updatedInventory };
      });
    },

    setOpenMenu: (menu) => {
      set({ openMenu: menu });
    },

    sellMineral: (itemId: number, quantity: number) => {
      let value = 0;
      set((state) => {
        const existingItemIndex = state.inventory.findIndex(
          (item) => item.id === itemId,
        );
        const item = state.inventory[existingItemIndex];
        if (!item) return state; // Item not found

        const sellQuantity = Math.min(quantity, item.quantity);

        const sellValue = TERRAIN_TYPES_MAP[item.id]?.sellValue ?? 0;

        value = sellQuantity * sellValue;

        const updatedInventory = [...state.inventory];

        if (sellQuantity >= item.quantity) {
          // Remove the item if selling all
          updatedInventory.splice(existingItemIndex, 1);
        } else {
          // Reduce quantity
          updatedInventory[existingItemIndex] = {
            ...item,
            quantity: item.quantity - sellQuantity,
          };
        }

        return {
          inventory: updatedInventory,
          currency: state.currency + value,
        };
      });

      return value;
    },

    purchaseUpgrade: (upgradeKey: PlayerUpgradeNumberKey, amount: number) => {
      const state = get();

      const { step } = PLAYER_UPGRADES[upgradeKey];
      const stepCost = 1; // TODO
      const totalCost = stepCost * amount;
      const upgradeDelta = step * amount;

      if (state.currency < totalCost) return false;

      set((state) => ({
        playerUpgrades: {
          ...state.playerUpgrades,
          [upgradeKey]: state.playerUpgrades[upgradeKey] + upgradeDelta,
        },
        currency: state.currency - totalCost,
      }));

      return true;
    },

    // updateChunk: (chunk) => {
    //   set((state) => ({
    //     ...state,
    //     initialChunks: { ...state.initialChunks, [chunk.uid]: chunk },
    //   }));
    // },

    saveGame: async () => {
      const state = get();
      if (!state.user || !state.currentGameSaveId) {
        console.error("Cannot save game: No user or game-save");
        return;
      }

      await firestoreService.saveGame(
        state.user.id,
        state.currentGameSaveId,
        serializeGameState(state),
        GameEngine.voxelTerrain.chunks,
      );
    },

    updateSettings: (settings: Partial<GameStoreValues["settings"]>) => {
      set((state) => ({
        settings: { ...state.settings, ...settings },
      }));
    },

    updatePlayerUpgrade: <Key extends PlayerUpgradeKey>(
      upgrade: Key,
      value: PlayerUpgradeValues[Key],
    ) => {
      set((state) => ({
        playerUpgrades: {
          ...state.playerUpgrades,
          [upgrade]: value,
        },
      }));
    },

    setLoadout: (loadoutNumber: number) => {
      const loadout = LOADOUTS[loadoutNumber];
      if (!loadout) return;

      const asString = Object.entries(loadout)
        .map(([key, value]) => `${startCase(key)}: ${value}`)
        .join("\n");

      toast(`Loaded loadout #${loadoutNumber}`, {
        descriptionClassName: "whitespace-pre-wrap",
        description: asString,
      });

      set({ playerUpgrades: loadout });
    },
  }),
);

const LOADOUTS: { [loadoutNumber in number]?: PlayerUpgradeValues } = {
  1: PLAYER_UPGRADES_DEFAULTS,
  2: {
    ...PLAYER_UPGRADES_DEFAULTS,
    miningMode: "laser",
    drillStrength: 10,
    drillRadius: 20,
    drillOffset: 1000,
    cooldownRate: 2.0,
    heatResistance: 1000,
    fuelCapacity: 10000,
  },
  3: {
    ...PLAYER_UPGRADES_DEFAULTS,
    miningMode: "grenade",
  },
};

function serializeGameState(state: GameStoreValues) {
  return {
    player_position: state.playerPosition,
    stats: state.playerStats,
    upgrades: state.playerUpgrades,
    inventory: state.inventory,
    currency: state.currency,
    settings: state.settings,
    base_seed: state.baseSeed,
  };
}
export type SerializedGameState = ReturnType<typeof serializeGameState>;

function deserializeGameState(
  state: SerializedGameState,
): Partial<GameStoreValues> {
  return {
    playerPosition: state.player_position,
    playerStats: state.stats,
    playerUpgrades: state.upgrades,
    inventory: state.inventory,
    currency: state.currency,
    settings: state.settings,
    baseSeed: state.base_seed,
  };
}
